import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Filter, X, Grid3X3, List } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from '../components/ui/sheet';
import ProductCard from '../components/ProductCard';
import Breadcrumbs from '../components/Breadcrumbs';
import BrandFilterBar from '../components/BrandFilterBar';
import { ProductCardSkeleton } from '../components/Skeleton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);
    const [viewMode, setViewMode] = useState('grid');
    const [brandData, setBrandData] = useState(null);
    
    // URL-persisted filters (survive reload, share, Back button)
    const selectedCategory = searchParams.get('category') || '';
    const selectedSubcategory = searchParams.get('subcategory') || '';
    const selectedSubSubcategory = searchParams.get('sub_subcategory') || '';
    const selectedBrand = searchParams.get('brand') || '';
    
    // Filters
    const [minPrice, setMinPrice] = useState(searchParams.get('min_price') || '');
    const [maxPrice, setMaxPrice] = useState(searchParams.get('max_price') || '');
    const [inStockOnly, setInStockOnly] = useState(searchParams.get('in_stock') === '1');
    const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
    const [sortBy, setSortBy] = useState('default');
    const [currentPage, setCurrentPage] = useState(1);

    // Always start from the top when the page number changes (desktop + mobile)
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, [currentPage]);

    const updateFilters = (updates) => {
        const p = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([k, v]) => {
            if (v) p.set(k, v); else p.delete(k);
        });
        setSearchParams(p);
        setCurrentPage(1);
    };

    useEffect(() => {
        const controller = new AbortController();
        const fetchFilters = async () => {
            try {
                const catRes = await axios.get(`${API}/categories`, { signal: controller.signal });
                setCategories(catRes.data);
            } catch (error) {
                if (error.name !== 'CanceledError') {
                    console.error('Error fetching filters:', error);
                }
            }
        };
        fetchFilters();
        return () => controller.abort();
    }, []);

    const currentCategory = categories.find(c => c.category_id === selectedCategory);

    // Fetch brands for the selected category (when brand organization is enabled)
    useEffect(() => {
        const subBrandEnabled = selectedSubcategory && currentCategory?.subcategory_brand_settings?.[selectedSubcategory]?.organize_by_brands;
        if (!currentCategory?.slug || (!currentCategory?.organize_by_brands && !subBrandEnabled)) {
            setBrandData(null);
            return;
        }
        const controller = new AbortController();
        axios.get(`${API}/categories/${currentCategory.slug}/brands`, {
            params: selectedSubcategory ? { subcategory: selectedSubcategory } : {},
            signal: controller.signal
        })
            .then(res => setBrandData(res.data))
            .catch(() => {});
        return () => controller.abort();
    }, [currentCategory, selectedSubcategory]);

    // SEO title when a brand is selected
    useEffect(() => {
        if (selectedBrand && brandData?.enabled && currentCategory) {
            const b = (brandData.brands || []).find(x => x.slug === selectedBrand);
            if (b) {
                document.title = b.seo_title || `${currentCategory.name} ${b.name} | Вкусът на Турция`;
                return;
            }
        }
        document.title = 'Вкусът на Турция';
    }, [selectedBrand, brandData, currentCategory]);

    useEffect(() => {
        const controller = new AbortController();
        const fetchProducts = async () => {
            setLoading(true);
            try {
                const params = {
                    page: currentPage,
                    limit: 12,
                    sort: sortBy
                };
                
                if (selectedCategory) params.category = selectedCategory;
                if (selectedSubcategory) params.subcategory = selectedSubcategory;
                if (selectedSubSubcategory) params.sub_subcategory = selectedSubSubcategory;
                if (selectedBrand) params.brand = selectedBrand;
                if (minPrice) params.min_price = parseFloat(minPrice);
                if (maxPrice) params.max_price = parseFloat(maxPrice);
                if (inStockOnly) params.in_stock = true;
                if (searchQuery) params.search = searchQuery;
                if (searchParams.get('featured')) params.featured = true;
                if (searchParams.get('new')) params.new = true;
                if (searchParams.get('bestseller')) params.bestseller = true;
                if (searchParams.get('on_sale')) params.on_sale = true;

                const response = await axios.get(`${API}/products`, { params, signal: controller.signal });
                setProducts(response.data.products);
                setTotalPages(response.data.pages);
                setTotalProducts(response.data.total);
            } catch (error) {
                if (error.name !== 'CanceledError') {
                    console.error('Error fetching products:', error);
                }
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
        return () => controller.abort();
    }, [selectedCategory, selectedSubcategory, selectedSubSubcategory, selectedBrand, minPrice, maxPrice, inStockOnly, searchQuery, sortBy, currentPage, searchParams]);

    const clearFilters = () => {
        setMinPrice('');
        setMaxPrice('');
        setInStockOnly(false);
        setSearchQuery('');
        setSearchParams({});
        setCurrentPage(1);
    };

    const activeFiltersCount = [selectedCategory, selectedSubcategory, selectedSubSubcategory, selectedBrand, minPrice, maxPrice, inStockOnly].filter(Boolean).length;

    const FilterSidebar = () => (
        <div className="space-y-6">
            {/* Categories */}
            <div>
                <h3 className="font-semibold mb-3 text-amber-800 dark:text-amber-400">Категории</h3>
                <div className="space-y-2">
                    {categories.map((category) => (
                        <div key={category.category_id}>
                            <button
                                onClick={() => {
                                    updateFilters({
                                        category: selectedCategory === category.category_id ? '' : category.category_id,
                                        subcategory: '',
                                        sub_subcategory: '',
                                        brand: ''
                                    });
                                }}
                                className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors font-medium ${
                                    selectedCategory === category.category_id 
                                        ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white' 
                                        : 'hover:bg-amber-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
                                }`}
                                data-testid={`filter-category-${category.slug}`}
                            >
                                {category.name}
                            </button>
                            
                            {/* Subcategories - show for any selected category with subcategories */}
                            {selectedCategory === category.category_id && category.subcategories?.length > 0 && (
                                <div className="ml-3 mt-2 space-y-1 border-l-2 border-amber-200 dark:border-amber-700 pl-3">
                                    {category.subcategories.map((sub) => {
                                        const subSubs = (category.sub_subcategories && category.sub_subcategories[sub]) || [];
                                        return (
                                        <div key={sub}>
                                            <button
                                                onClick={() => updateFilters({ subcategory: selectedSubcategory === sub ? '' : sub, sub_subcategory: '', brand: '' })}
                                                className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                                                    selectedSubcategory === sub 
                                                        ? 'bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 font-medium' 
                                                        : 'text-slate-600 dark:text-slate-400 hover:text-amber-700 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-slate-700'
                                                }`}
                                                data-testid={`filter-subcategory-${sub}`}
                                            >
                                                {sub}
                                            </button>
                                            {selectedSubcategory === sub && subSubs.length > 0 && (
                                                <div className="ml-3 mt-1 space-y-1 border-l-2 border-amber-100 dark:border-amber-800 pl-3">
                                                    {subSubs.map((ss) => (
                                                        <button
                                                            key={ss}
                                                            onClick={() => updateFilters({ sub_subcategory: selectedSubSubcategory === ss ? '' : ss })}
                                                            className={`w-full text-left px-3 py-1.5 text-xs rounded-lg transition-colors ${
                                                                selectedSubSubcategory === ss
                                                                    ? 'bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 font-medium'
                                                                    : 'text-slate-500 dark:text-slate-400 hover:text-amber-700 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-slate-700'
                                                            }`}
                                                            data-testid={`filter-subsub-${ss}`}
                                                        >
                                                            {ss}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Price Range */}
            <div>
                <h3 className="font-semibold mb-3 text-amber-800">Цена (EUR)</h3>
                <div className="flex items-center gap-2">
                    <Input
                        type="number"
                        placeholder="От"
                        value={minPrice}
                        onChange={(e) => setMinPrice(e.target.value)}
                        className="h-10 border-amber-200 focus:border-amber-400"
                        data-testid="filter-min-price"
                    />
                    <span className="text-slate-400">-</span>
                    <Input
                        type="number"
                        placeholder="До"
                        value={maxPrice}
                        onChange={(e) => setMaxPrice(e.target.value)}
                        className="h-10 border-amber-200 focus:border-amber-400"
                        data-testid="filter-max-price"
                    />
                </div>
            </div>

            {/* Availability */}
            <div>
                <label className="flex items-center gap-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={inStockOnly}
                        onChange={(e) => setInStockOnly(e.target.checked)}
                        className="h-4 w-4 accent-amber-500"
                        data-testid="filter-in-stock"
                    />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Само налични</span>
                </label>
            </div>

            {/* Clear Filters */}
            {activeFiltersCount > 0 && (
                <Button 
                    variant="outline" 
                    onClick={clearFilters}
                    className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="clear-filters-btn"
                >
                    <X className="h-4 w-4 mr-2" />
                    Изчисти филтрите ({activeFiltersCount})
                </Button>
            )}
        </div>
    );

    return (
        <div className="min-h-screen bg-gradient-to-b from-amber-50/50 to-white dark:from-slate-900 dark:to-slate-800">
            {/* Header */}
            <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border-b border-amber-100 dark:border-slate-700">
                <div className="container-main py-8">
                    <Breadcrumbs customItems={(() => {
                        const items = [{ path: '/products', name: 'Продукти' }];
                        if (currentCategory) {
                            items.push({ path: `/products?category=${currentCategory.slug}`, name: currentCategory.name });
                            if (selectedSubcategory) {
                                items.push({ path: `/products?category=${currentCategory.slug}&subcategory=${encodeURIComponent(selectedSubcategory)}`, name: selectedSubcategory });
                            }
                            const selBrand = selectedBrand && brandData?.brands?.find(b => b.slug === selectedBrand);
                            if (selBrand) items.push({ path: '#', name: selBrand.name });
                        }
                        return items;
                    })()} />
                    <h1 className="font-serif text-3xl md:text-4xl font-bold text-amber-800 dark:text-amber-400">
                        {searchQuery ? `Резултати за "${searchQuery}"` : 
                         currentCategory ? `${currentCategory.name}${selectedBrand && brandData?.brands?.find(b => b.slug === selectedBrand) ? ' ' + brandData.brands.find(b => b.slug === selectedBrand).name : ''}` : 'Всички продукти'}
                    </h1>
                    {selectedSubcategory && (
                        <p className="text-amber-600 dark:text-amber-400 mt-2">{selectedSubcategory}</p>
                    )}
                </div>
            </div>

            <div className="container-main py-8">
                <div className="flex gap-8">
                    {/* Sidebar - Desktop */}
                    <aside className="hidden lg:block w-64 flex-shrink-0">
                        <div className="sticky top-32 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm p-5 rounded-2xl border border-amber-100 dark:border-slate-700 shadow-lg">
                            <FilterSidebar />
                        </div>
                    </aside>

                    {/* Main Content */}
                    <div className="flex-1 min-w-0">
                        {/* Brand filter bar (when enabled for the category) */}
                        <BrandFilterBar
                            data={brandData}
                            selected={selectedBrand}
                            onSelect={(slug) => updateFilters({ brand: slug })}
                        />

                        {/* Toolbar */}
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-6 bg-white/80 backdrop-blur-sm p-3 sm:p-4 rounded-xl border border-amber-100">
                            <div className="flex items-center gap-3 min-w-0">
                                {/* Mobile Filter Button */}
                                <Sheet>
                                    <SheetTrigger asChild>
                                        <Button variant="outline" className="lg:hidden border-amber-300 text-amber-700" data-testid="mobile-filter-btn">
                                            <Filter className="h-4 w-4 mr-2" />
                                            Филтри
                                            {activeFiltersCount > 0 && (
                                                <span className="ml-2 bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                                                    {activeFiltersCount}
                                                </span>
                                            )}
                                        </Button>
                                    </SheetTrigger>
                                    <SheetContent side="left" className="w-[85vw] sm:w-80 overflow-y-auto">
                                        <SheetHeader>
                                            <SheetTitle className="text-amber-800">Филтри</SheetTitle>
                                        </SheetHeader>
                                        <div className="mt-6">
                                            <FilterSidebar />
                                        </div>
                                    </SheetContent>
                                </Sheet>

                                <p className="text-slate-600 text-sm font-medium">
                                    {totalProducts} продукта
                                </p>
                            </div>

                            <div className="flex items-center gap-3 flex-shrink-0">
                                {/* Sort */}
                                <Select value={sortBy} onValueChange={setSortBy}>
                                    <SelectTrigger className="w-40 sm:w-44 border-amber-200" data-testid="sort-select">
                                        <SelectValue placeholder="Сортирай по" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="default">Подредени от магазина</SelectItem>
                                        <SelectItem value="newest">Най-нови</SelectItem>
                                        <SelectItem value="price_asc">Цена: ниска-висока</SelectItem>
                                        <SelectItem value="price_desc">Цена: висока-ниска</SelectItem>
                                        <SelectItem value="name">Име</SelectItem>
                                        <SelectItem value="popular">Най-популярни</SelectItem>
                                    </SelectContent>
                                </Select>

                                {/* View Toggle */}
                                <div className="hidden md:flex items-center border border-amber-200 rounded-lg overflow-hidden">
                                    <button
                                        onClick={() => setViewMode('grid')}
                                        className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-amber-100 text-amber-700' : 'text-slate-500 hover:bg-amber-50'}`}
                                        data-testid="view-grid-btn"
                                    >
                                        <Grid3X3 className="h-5 w-5" />
                                    </button>
                                    <button
                                        onClick={() => setViewMode('list')}
                                        className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-amber-100 text-amber-700' : 'text-slate-500 hover:bg-amber-50'}`}
                                        data-testid="view-list-btn"
                                    >
                                        <List className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Products Grid */}
                        {loading ? (
                            <div className="grid gap-4 sm:gap-6 grid-cols-1 min-[420px]:grid-cols-2 lg:grid-cols-3">
                                {[...Array(6)].map((_, i) => (
                                    <ProductCardSkeleton key={i} />
                                ))}
                            </div>
                        ) : products.length === 0 ? (
                            <div className="text-center py-16 bg-white/80 backdrop-blur-sm rounded-2xl border border-amber-100">
                                <p className="text-slate-500 text-lg">Няма намерени продукти</p>
                                <Button 
                                    variant="outline" 
                                    onClick={clearFilters}
                                    className="mt-4 border-amber-300 text-amber-700 hover:bg-amber-50"
                                >
                                    Изчисти филтрите
                                </Button>
                            </div>
                        ) : (
                            <div className={`grid ${
                                viewMode === 'grid' 
                                    ? 'gap-4 sm:gap-6 grid-cols-1 min-[420px]:grid-cols-2 lg:grid-cols-3' 
                                    : 'gap-4 sm:gap-6 grid-cols-1'
                            }`}>
                                {products.map((product) => (
                                    <ProductCard key={product.product_id} product={product} />
                                ))}
                            </div>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-center gap-2 mt-12">
                                <Button
                                    variant="outline"
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    className="border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                                    data-testid="prev-page-btn"
                                >
                                    Предишна
                                </Button>
                                <span className="px-4 py-2 text-sm font-medium text-slate-600">
                                    Страница {currentPage} от {totalPages}
                                </span>
                                <Button
                                    variant="outline"
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    className="border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                                    data-testid="next-page-btn"
                                >
                                    Следваща
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductsPage;
